import { css } from 'lit';

export default css`
  :host {
    display: flex;
    width: stretch;
    width: -moz-available;
    width: -webkit-fill-available;
    align-items: stretch;
    font-family: var(--font-family-base, 'Inter, Arial, sans-serif');
    --media-height: 240px;
    --footer-max-height: 50px;
    margin: 0 auto;
  }

  .card {
    position: relative;
    background: var(--card-bg, #ffffff);
    box-shadow: var(--card-shadow, 0px 6px 14px -6px rgba(19, 25, 39, 0.12), 0px 10px 32px -4px rgba(19, 25, 39, 0.1));
    border-radius: var(--radius-md, 12px);
    overflow: hidden;
    cursor: pointer;
  }

  .card.vertical {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    height: var(--vertical-card-height);
  }

  .card.horizontal {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    width: 100%;
    height: var(--horizontal-card-height);
  }

  .media {
    position: relative;
    width: auto;
    overflow: hidden;
  }

  .vertical .media {
    max-height: var(--media-height);
  }

  .type-damCard.vertical .media {
    max-height: unset;
    height: 100%;
  }

  ::slotted([slot="media"]) {
    object-fit: cover;
    object-position: center;
    width: 100%;
    height: -webkit-fill-available;
    box-sizing: border-box;
  }

  .type-damCard.vertical .content-wrapper {
    max-height: 200px;
  }

  .horizontal .media {
    height: var(--horizontal-card-height);
    min-width: var(--horizontal-card-height);
  }

  .type-keywordCard .media {
    display: none;
  }

  ::slotted([slot="media"]) {
    object-fit: cover;
    object-position: center;
  }

  .content-wrapper {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    box-sizing: border-box;
  }

  .body {
    display: flex;
    flex-direction: column;
    padding: var(--spacing-md, 16px);
    gap: var(--spacing-lg, 24px);
    width: 100%;
    box-sizing: border-box;
  }

  .vertical .body {
    overflow: hidden;
  }

  .horizontal .body {
    height: 150px;
    gap: var(--spacing-xs, 4px);
    width: auto;
    font-size: 10pt;
  }

  .type-keywordCard .body {
    gap: var(--spacing-xs, 4px);
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: space-between;
  }

  ::slotted([slot="body"]) {
    -webkit-line-clamp: 3;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-word;
  }

  .footer {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 0 var(--spacing-md, 16px) var(--spacing-md, 16px);
    width: 100%;
    box-sizing: border-box;
  }

  .vertical .footer {
    max-height: var(--footer-max-height);
  }

  .type-keywordCard .footer {
    padding: 0px;
  }

  ::slotted([slot="footer"]) {
    width: 100%;
  }

  .type-keywordCard.state-expired ::slotted([slot="body"]),
  .type-keywordCard.state-expired ::slotted([slot="footer"]) {
    color: var(--color-gray-3, #d7d4d2);
  }

  .overlay {
    position: absolute;
    inset: 0;
    background: var(--overlay-bg, rgba(0, 0, 0, 0.1));
    display: flex;
    justify-content: center;
    align-items: center;
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: 10;
    pointer-events: none;
  }

  .card:hover .overlay {
    opacity: 1;
    pointer-events: auto;
  }

  .actions-container {
    position: relative;
    height: 100%;
    width: 100%;
    display: flex;
    gap: 8px;
    z-index: 10;
    pointer-events: auto;
  }

  ::slotted([slot="actions"]) {
    position: absolute;
    top: var(--spacing-md, 1rem);
    right: var(--spacing-md, 1rem);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: var(--action-bg, rgba(255, 255, 255, 0.8));
    border-radius: var(--radius-sm, 8px);
    width: 40px;
    height: 40px;
    box-shadow: var(--action-shadow, 0 0 8px rgba(0, 0, 0, 0.2));
    border: none;
    cursor: pointer;
  }

  .horizontal .actions-container ::slotted([slot="actions"]) {
    right: unset;
    left: var(--spacing-md, 1rem);
  }

  :host([has-selector]) .card {
    animation: select-pop 250ms ease-out forwards, select-glow 900ms ease-out 40ms;
    border: 2px solid var(--color-prism-indigo-normal);
  }

  :host([has-selector]) .card::after {
    content: "";
    position: absolute; inset: +2px;
    border-radius: var(--radius-md);
    pointer-events: none;
    animation: select-ring 700ms cubic-bezier(.22,.8,.2,1);
  }

  @keyframes select-pop {
    0% { transform: scale(.98); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
  }
  @keyframes select-glow {
    0%   { box-shadow: 0 6px 16px rgba(0,0,0,.08); }
    100% { box-shadow: 0 10px 28px rgba(0,0,0,.16); }
  }
  @keyframes select-ring {
    0%   { box-shadow: 0 0 0 6px rgba(78,156,255,.35); opacity: 1; }
    80%  { box-shadow: 0 0 0 12px rgba(78,156,255,.15); opacity: .6; }
    100% { box-shadow: 0 0 0 18px rgba(78,156,255,0); opacity: 0; }
  }

  @media (prefers-reduced-motion: reduce) {
    :host([selected]) .card,
    :host([selected]) .card::after { animation: none; }
  }

  // Keywords

  .card.type-keywordCard.state-expired {
    background: var(--color-gray-1, #f7f5f4);
    border-color: var(--color-gray-3, #d7d4d2);
  }

  .card.type-keywordCard .body ::slotted(*) {
    display: flex;
    justify-content: start;
    align-items: center;
    margin-bottom: 8px;
    gap: 10px;
    color: var(--color-black);
    flex-wrap: wrap;
    height: auto;
    width: 100%;
	}
  .card.type-keywordCard .body ::slotted(.title) {
    display: flex;
    justify-content: center;
    align-self: start;
    height: 40%;
    width: 100%;
    font-size: 24px;
    font-weight: 600;
    background: var(--color-gray-1);
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-400);
  }
	.card.type-keywordCard .title.expired {
		color: var(--color-black);
	}

	.card.type-keywordCard .title div {
		position: relative;
		text-align: center;
		grid-column: 2;
		margin: 0;
				
		font-family: Inter;
		font-size: 16px;
		font-style: normal;
		font-weight: 600;
		line-height: 24px;
	}
	.card.type-keywordCard .title studio-badge {
		grid-column: 3;
		justify-self: end;
	}

	.card.type-keywordCard .uri{
		display: flex;
		flex-direction: row;
		align-items: center;
		gap: 4px;
		color: var(--color-black);
	}

	.card.type-keywordCard .uri.expired {
		color: var(--color-gray-4);
	}

	.card.type-keywordCard .uri .uri-text {
		flex: 1 1 0;
		min-width: 0;
		overflow: hidden;
		white-space: nowrap;
    text-overflow: ellipsis;	
	}

	.card.type-keywordCard .uri .pending {
		display: none;
	}

	.card.type-keywordCard .updated {
		display: flex;
		flex-direction: row;
		align-items: center;
		gap: 4px;
		color: var(--color-black);
	}

	.card.type-keywordCard .updated.expired {
		color: var(--color-gray-4);
	}
	
	.card.type-keywordCard .updated .updated-text {
		flex: 1 1 0;
		min-width: 0;
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
	}

	.card.type-keywordCard .date {
		display: flex;
		flex-direction: row;
		align-items: center;
		gap: 4px;
		color: var(--color-black);
	}

	.card.type-keywordCard .date.expired {
		color: var(--color-gray-4);
	}

	.card.type-keywordCard .icon.expires-soon {
		color: var(--color-error);
	}
	.card.type-keywordCard .icon.starts-soon {
		color: var(--color-success);
	}
	.card.type-keywordCard .icon.none {
		display: none;
	}
  @media (max-width: 1024px) {
    .card.horizontal {
      width: calc(100vw - var(--spacing-lg) - var(--spacing-lg));
    }
  }
  @media (max-width: 768px) {
    :host {
      width: 100%;
    }
    .card.horizontal {
      width: calc(100vw - var(--spacing-md) - var(--spacing-md));
    }
  }
`;
